import { createAsyncThunk, createEntityAdapter, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { CmtsReduxThunkExtra, CmtsSerializableError } from "./thunkExtra.js";
import { readReduxString, serializeReduxError, serializeRepositoryFailure } from "./thunkExtra.js";

export type CmtsMasterDataEntity = {
  readonly id: string;
  readonly entityType: string;
  readonly code?: string | undefined;
  readonly name?: string | undefined;
  readonly status?: string | undefined;
  readonly tenantCode?: string | undefined;
  readonly updatedAtUtc?: string | undefined;
};

const masterDataAdapter = createEntityAdapter<CmtsMasterDataEntity>();

export type FetchCmtsMasterDataPageArgs = { readonly entityType: string; readonly tenantId?: string | undefined };

function toMasterDataEntity(entityType: string, row: unknown): CmtsMasterDataEntity {
  const id = readReduxString(row, ["id", "code", "value"], `${entityType}-${crypto.randomUUID?.() ?? Date.now().toString(36)}`);
  return {
    id,
    entityType,
    code: readReduxString(row, ["code", "value"], id),
    name: readReduxString(row, ["name", "label", "displayName"], id),
    status: readReduxString(row, ["status", "lifecycleStatus"], "active"),
    tenantCode: readReduxString(row, ["tenantCode", "TenantCode"], ""),
    updatedAtUtc: readReduxString(row, ["updatedAtUtc", "updatedAt", "modifiedAt"], ""),
  };
}

export const fetchCmtsMasterDataPage = createAsyncThunk<{ readonly entityType: string; readonly records: readonly CmtsMasterDataEntity[] }, FetchCmtsMasterDataPageArgs, { extra: CmtsReduxThunkExtra; rejectValue: CmtsSerializableError }>(
  "masterData/fetchPage",
  async (args, thunkApi) => {
    try {
      if (!thunkApi.extra.repositories) {
        return thunkApi.rejectWithValue({ message: "Repository suite is not available for master-data page refresh.", atUtc: new Date().toISOString() });
      }
      const result = await thunkApi.extra.repositories.masterData.list(args.entityType, args.tenantId);
      if (!result.ok) {
        return thunkApi.rejectWithValue(serializeRepositoryFailure(result.diagnostics, "Master-data page refresh failed."));
      }
      return { entityType: args.entityType, records: result.data.map((row) => toMasterDataEntity(args.entityType, row)) };
    } catch (error: unknown) {
      return thunkApi.rejectWithValue(serializeReduxError(error, "Master-data page refresh failed."));
    }
  },
);


export type CmtsMasterDataOperation = "create" | "update" | "delete" | "activate" | "deactivate";

export type CmtsMasterDataState = {
  readonly entities: ReturnType<typeof masterDataAdapter.getInitialState>;
  readonly currentEntityType?: string | undefined;
  readonly selectedRecordId?: string | undefined;
  readonly listLoading: boolean;
  readonly detailLoading: boolean;
  readonly validationState?: { readonly valid: boolean; readonly messages: string[]; readonly atUtc: string } | undefined;
  readonly dependencyCheckState?: { readonly blocked: boolean; readonly blockers: string[]; readonly atUtc: string } | undefined;
  readonly lastCrudOperation?: { readonly operation: CmtsMasterDataOperation; readonly status: "idle" | "pending" | "succeeded" | "failed"; readonly atUtc: string } | undefined;
  readonly error?: CmtsSerializableError | undefined;
};

const initialState: CmtsMasterDataState = {
  entities: masterDataAdapter.getInitialState(),
  listLoading: false,
  detailLoading: false,
};

const masterDataSlice = createSlice({
  name: "masterData",
  initialState,
  reducers: {
    masterDataEntitySelected(state, action: PayloadAction<string | undefined>) {
      state.currentEntityType = action.payload;
      state.selectedRecordId = undefined;
      state.error = undefined;
    },
    masterDataListLoadStarted(state) {
      state.listLoading = true;
      state.error = undefined;
    },
    masterDataListLoaded(state, action: PayloadAction<{ entityType: string; records: readonly CmtsMasterDataEntity[] }>) {
      state.currentEntityType = action.payload.entityType;
      masterDataAdapter.setAll(state.entities, action.payload.records);
      state.listLoading = false;
      state.error = undefined;
    },
    masterDataRecordSelected(state, action: PayloadAction<string | undefined>) {
      state.selectedRecordId = action.payload;
    },
    masterDataRecordUpserted(state, action: PayloadAction<CmtsMasterDataEntity>) {
      masterDataAdapter.upsertOne(state.entities, action.payload);
      state.lastCrudOperation = { operation: "update", status: "succeeded", atUtc: new Date().toISOString() };
    },
    masterDataRecordRemoved(state, action: PayloadAction<string>) {
      masterDataAdapter.removeOne(state.entities, action.payload);
      if (state.selectedRecordId === action.payload) state.selectedRecordId = undefined;
      state.lastCrudOperation = { operation: "delete", status: "succeeded", atUtc: new Date().toISOString() };
    },
    masterDataValidationRecorded(state, action: PayloadAction<{ valid: boolean; messages: readonly string[] }>) {
      state.validationState = { valid: action.payload.valid, messages: [...action.payload.messages], atUtc: new Date().toISOString() };
    },
    masterDataDependencyCheckRecorded(state, action: PayloadAction<{ blocked: boolean; blockers: readonly string[] }>) {
      state.dependencyCheckState = { blocked: action.payload.blocked, blockers: [...action.payload.blockers], atUtc: new Date().toISOString() };
    },
    masterDataOperationStarted(state, action: PayloadAction<CmtsMasterDataOperation>) {
      state.lastCrudOperation = { operation: action.payload, status: "pending", atUtc: new Date().toISOString() };
      state.error = undefined;
    },
    masterDataOperationFailed(state, action: PayloadAction<CmtsSerializableError>) {
      if (state.lastCrudOperation) state.lastCrudOperation = { ...state.lastCrudOperation, status: "failed", atUtc: new Date().toISOString() };
      state.error = action.payload;
    },
    masterDataCleared(state) {
      masterDataAdapter.removeAll(state.entities);
      state.currentEntityType = undefined;
      state.selectedRecordId = undefined;
      state.listLoading = false;
      state.detailLoading = false;
      state.validationState = undefined;
      state.dependencyCheckState = undefined;
      state.lastCrudOperation = undefined;
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCmtsMasterDataPage.pending, (state, action) => {
        state.currentEntityType = action.meta.arg.entityType;
        state.listLoading = true;
        state.error = undefined;
      })
      .addCase(fetchCmtsMasterDataPage.fulfilled, (state, action) => {
        state.currentEntityType = action.payload.entityType;
        masterDataAdapter.setAll(state.entities, action.payload.records);
        state.listLoading = false;
        state.error = undefined;
      })
      .addCase(fetchCmtsMasterDataPage.rejected, (state, action) => {
        state.currentEntityType = action.meta.arg.entityType;
        state.listLoading = false;
        state.error = action.payload ?? serializeReduxError(action.error, "Master-data page refresh failed.");
      });
  },
});

export const masterDataEntityAdapter = masterDataAdapter;
export const masterDataActions = masterDataSlice.actions;
export const masterDataReducer = masterDataSlice.reducer;
